import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';

import counterpartyAccountByIdentityManager from '@gasbi/shared/common/utils/counterpartyAccountByIdentityManager';
import representativeByIdentityManager from '@gasbi/shared/common/utils/representativeByIdentityManager';

import { useLazyGetCurrentUserQuery } from '@/features/auth/authApi';
import { useLazyGetCounterpartyAccountByIdentityIdQuery } from '@/features/counterpartyAccounts/counterpartyAccountsApi';
import { CounterpartyAccountByIdentityId } from '@/features/counterpartyAccounts/types';
import { useLazyGetRepresentativeByIdentityIdQuery } from '@/features/representatives/representativesApi';
import { Representative } from '@/features/representatives/types';
import NotAuthorized from '@/pages/NotAuthorized';
import { AppAuthProvider } from '@/providers/AppAuthProvider';

import { Loader } from '@gasbi/shared';

/**
 * Cookie (BFF) session bootstrap (GSB-7560): one GET /api/user behind the HttpOnly cookie,
 * then the cabinet's own view of that identity (representative and counterparty account),
 * which the pages read from the shared identity managers. A 401 is handled by the http
 * interceptor (navigation to /api/login); a 403 means the identity signed in fine but has no
 * representative in this cabinet.
 */
export const AuthInitProvider: FC<PropsWithChildren> = ({ children }) => {
  const [fetchCurrentUser, { data: user, error }] = useLazyGetCurrentUserQuery();
  const [fetchRepresentative] = useLazyGetRepresentativeByIdentityIdQuery();
  const [fetchCounterpartyAccount] = useLazyGetCounterpartyAccountByIdentityIdQuery();
  const [isReady, setIsReady] = useState(false);

  const isForbidden = (error as { status?: number } | undefined)?.status === 403;

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    (async () => {
      const [representative, account] = await Promise.all([
        fetchRepresentative(user.id).unwrap(),
        fetchCounterpartyAccount(user.id).unwrap(),
      ]);
      if (cancelled) return;

      representativeByIdentityManager.setState((representative as Representative)?.id);
      if (!counterpartyAccountByIdentityManager.getState()) {
        counterpartyAccountByIdentityManager.setState(
          account as CounterpartyAccountByIdentityId,
        );
      }
      setIsReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [user, fetchRepresentative, fetchCounterpartyAccount]);

  const value = useMemo(
    () =>
      user && {
        user,
        logout: () => {
          window.location.assign('/api/logout?returnUrl=/');
        },
      },
    [user],
  );

  if (isForbidden) {
    return <NotAuthorized />;
  }

  if (!value || !isReady) {
    return <Loader />;
  }

  return <AppAuthProvider value={value}>{children}</AppAuthProvider>;
};
